import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { generateMarketReport } from '../api/market'
import type { MarketReportResponse } from '../types/market'

const POLL_INTERVAL_MS = 15_000

export interface Notice {
  id: number
  message: string
  tone: 'success' | 'error'
}

interface PendingReportsContextValue {
  /** Register an asset as "generating" — kicks off background polling until complete. */
  startTracking: (asset: string) => void
  /** Latest completed report per asset — panels can use this to update content without re-triggering. */
  latestByAsset: Record<string, MarketReportResponse>
  /** Assets currently generating in the background. */
  pending: Set<string>
  /** In-flight toasts/notifications. */
  notices: Notice[]
  dismissNotice: (id: number) => void
}

const Ctx = createContext<PendingReportsContextValue | null>(null)

export function PendingReportsProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Set<string>>(new Set())
  const [latestByAsset, setLatestByAsset] = useState<Record<string, MarketReportResponse>>({})
  const [notices, setNotices] = useState<Notice[]>([])
  const pendingRef = useRef<Set<string>>(new Set())
  const pollTimerRef = useRef<number | null>(null)
  const noticeIdRef = useRef(1)

  pendingRef.current = pending

  const pushNotice = useCallback((message: string, tone: Notice['tone']) => {
    const id = noticeIdRef.current++
    setNotices(prev => [...prev, { id, message, tone }])
    window.setTimeout(() => {
      setNotices(prev => prev.filter(n => n.id !== id))
    }, 6000)
  }, [])

  const dismissNotice = useCallback((id: number) => {
    setNotices(prev => prev.filter(n => n.id !== id))
  }, [])

  const pollOnce = useCallback(async () => {
    const assets = Array.from(pendingRef.current)
    if (assets.length === 0) return
    for (const asset of assets) {
      try {
        const r = await generateMarketReport(asset)
        setLatestByAsset(prev => ({ ...prev, [asset]: r }))
        if (!r.pending) {
          setPending(prev => {
            const next = new Set(prev)
            next.delete(asset)
            return next
          })
          pushNotice(`${asset} market report is ready`, 'success')
        }
      } catch (err) {
        setPending(prev => {
          const next = new Set(prev)
          next.delete(asset)
          return next
        })
        pushNotice(`${asset} market report failed: ${err instanceof Error ? err.message : 'unknown'}`, 'error')
      }
    }
  }, [pushNotice])

  useEffect(() => {
    if (pending.size === 0) {
      if (pollTimerRef.current !== null) {
        window.clearInterval(pollTimerRef.current)
        pollTimerRef.current = null
      }
      return
    }
    if (pollTimerRef.current === null) {
      pollTimerRef.current = window.setInterval(pollOnce, POLL_INTERVAL_MS)
    }
    return () => {
      if (pollTimerRef.current !== null) {
        window.clearInterval(pollTimerRef.current)
        pollTimerRef.current = null
      }
    }
  }, [pending, pollOnce])

  const startTracking = useCallback((asset: string) => {
    setPending(prev => {
      if (prev.has(asset)) return prev
      const next = new Set(prev)
      next.add(asset)
      return next
    })
  }, [])

  return (
    <Ctx.Provider value={{ startTracking, latestByAsset, pending, notices, dismissNotice }}>
      {children}
    </Ctx.Provider>
  )
}

export function usePendingReports(): PendingReportsContextValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('usePendingReports must be used inside PendingReportsProvider')
  return v
}
